import { getPydanticAIChatHeaders } from "duck/api/axios";
import {
  CHAT_ENDPOINT_QUERY_PARAMS,
  SAFE_MODE_QUERY_PARAM,
} from "duck/constants";
import { ListThreadMessagesResponse } from "duck/endpoints/threads";
import {
  createThread,
  getListThreadMessagesRequestURI,
} from "duck/endpoints/threads/api";
import type { ClientAction } from "duck/ui/chat/actions";
import { sendMessage as sendSSEMessage } from "duck/ui/chat/serverSentEvents/chatClient";
import { createChatEventHandler } from "duck/ui/chat/serverSentEvents/messageHandler";
import type { TFunction } from "i18next";
import { mutate } from "swr";

import { getUserLocale } from "shared/locale/getUserLocale";
import { isSupportedFileType, sleep } from "shared/utils";
import { getAPIUrl } from "shared/utils/api-utils";

import type { SubthreadContent } from "./components/messages/SubthreadMessage/types";
import {
  CHAT_FILE_ALLOWED_EXTENSION_SET,
  CHAT_FILE_ALLOWED_MIME_TYPE_SET,
  CHAT_FILE_IMAGE_MIME_TYPES,
  CHAT_FILE_PIXELS_PER_TOKEN,
  CHAT_FILE_TEXT_BYTES_PER_TOKEN,
  DEFAULT_CHAT_BOTTOM_SCROLL_PADDING,
} from "./constants";
import type {
  Message,
  SSERequest,
  UserContentStructure,
} from "./serverSentEvents/types";
import { subthreadManager } from "./subthreadManager";
import {
  AssistantMessage,
  ChatMessage,
  ChatMessageRole,
  ChatMessageRoleType,
  INTERMEDIATE_OUTPUT_TOOL_NAME,
  RENDERABLE_MESSAGE_ROLES,
  SPAWN_SUBTHREAD_TOOL_NAME,
} from "./types";

const isSpawnSubthreadMessage = (message: Message): boolean =>
  message.type === ChatMessageRole.TOOL_RESPONSE &&
  message.metadata?.tool_name === SPAWN_SUBTHREAD_TOOL_NAME;

export const buildChatEndpointURL = (
  route: string,
  safeMode: boolean
): string => {
  const url = new URL(`${getAPIUrl("ai")}${route}`);
  Object.entries(CHAT_ENDPOINT_QUERY_PARAMS).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  if (safeMode) {
    url.searchParams.set(SAFE_MODE_QUERY_PARAM, "true");
  }

  return url.toString();
};

/**
 * Type guard to check if a message type is renderable in the UI.
 */
export const isRenderableMessageType = (
  type: string
): type is ChatMessageRoleType =>
  RENDERABLE_MESSAGE_ROLES.includes(type as ChatMessageRoleType);

/**
 * Extracts message contents from a message payload.
 * Handles various payload structures: { messages: [...] }, arrays, or single objects.
 */
export const extractMessageContents = (msg: Message): any[] => {
  const payload: any = msg.content ?? msg;
  if (payload?.messages) {
    return Array.isArray(payload.messages)
      ? payload.messages
      : [payload.messages];
  }

  if (Array.isArray(payload)) return payload;

  return [payload];
};

/**
 * Determines if a message should be rendered in the chat UI.
 * Centralizes filtering logic for tool_call and tool_response messages.
 */
export const shouldRenderMessage = (message: ChatMessage): boolean => {
  // tool_call: only render intermediate_output
  if (message.role === ChatMessageRole.TOOL_CALL) {
    return message.metadata?.tool_name === INTERMEDIATE_OUTPUT_TOOL_NAME;
  }

  // tool_response: only render spawn_subthread
  if (message.role === ChatMessageRole.TOOL_RESPONSE) {
    return message.metadata?.tool_name === SPAWN_SUBTHREAD_TOOL_NAME;
  }

  return true;
};

/**
 * Processes spawn_subthread tool responses and creates threads for them.
 * This is called during streaming whenever new tool_response messages arrive,
 * allowing subthreads to spawn immediately rather than waiting for the main thread to complete.
 * It creates the thread using the backend-provided thread_id and stores metadata.
 *
 * @param toolResponseMessages - Tool response messages to process
 * @param parentThreadId - The ID of the parent thread (current thread)
 * @param subthreadCreations - Map to track in-flight and completed subthread creations
 * @param t - Translation function forwarded to the subthread event handler
 * @param safeMode - Whether the parent request runs in safe mode; spawned subthreads inherit it
 * @param viewportIsMobile - The viewport state from the parent request
 * @returns Promise that resolves when all subthreads are created
 */
export const processSubthreadCreation = async (
  toolResponseMessages: Message[],
  parentThreadId: string,
  subthreadCreations: Map<string, Promise<void>>,
  t: TFunction,
  safeMode: boolean,
  chatRoute: string,
  viewportIsMobile?: SSERequest["viewport_is_mobile"],
  onClientAction?: (action: ClientAction) => void
): Promise<void> => {
  const subthreadMessages = toolResponseMessages.filter(
    isSpawnSubthreadMessage
  );

  if (subthreadMessages.length === 0) {
    return;
  }

  // Process all subthread creations in parallel
  await Promise.all(
    subthreadMessages.map(async (message) => {
      try {
        const content = message.content as SubthreadContent;

        const threadId = content.thread_id;
        if (!threadId) {
          throw new Error(
            "No thread_id or thread_id provided in subthread content"
          );
        }

        // if already in-flight or completed, await the existing promise
        if (subthreadCreations.has(threadId)) {
          await subthreadCreations.get(threadId);

          return;
        }

        // Mark subthread as streaming IMMEDIATELY (before any async work)
        // This prevents race condition where main thread ends before subthread is tracked
        subthreadManager.setStreaming(threadId, true);

        // create promise for this thread and store it before awaiting
        // on failure, remove from map so next patch can retry
        const creationPromise = (async () => {
          try {
            const newThread = await createThread(undefined, {
              thread_id: threadId,
              title: content.thread_title,
              parent_thread_id: parentThreadId,
            });

            // store the created thread ID in the message metadata
            if (!message.metadata) {
              message.metadata = {};
            }

            message.metadata.created_thread_id = newThread.id;

            console.log("Subthread created:", {
              threadId: newThread.id,
              title: content.thread_title,
            });

            // create assistant message placeholder
            const assistantMessage = createAssistantMessage(safeMode);

            // initialize subthread state with user message
            const userMessage = createUserMessage(content.utterance, safeMode);
            subthreadManager.updateMessages(newThread.id, [
              userMessage,
              assistantMessage,
            ]);
            subthreadManager.setStreaming(newThread.id, true);

            // create event handler that updates subthread state
            const eventHandler = createChatEventHandler({
              t,
              assistantMessageId: assistantMessage.id,
              setMessages: (updater) => {
                const currentState = subthreadManager.getState(newThread.id);
                const newMessages =
                  typeof updater === "function"
                    ? updater(currentState.messages)
                    : updater;
                subthreadManager.updateMessages(newThread.id, newMessages);
              },
              setIsStreaming: (isStreaming) => {
                subthreadManager.setStreaming(newThread.id, isStreaming);
              },
              onStreamingEnd: async () => {
                // refetch messages to get server IDs (needed for feedback to work)
                const requestURI = getListThreadMessagesRequestURI(chatRoute);
                await mutate(requestURI({ threadId: newThread.id }));
              },
            });

            sendSSEMessage({
              t,
              chatRoute,
              safeMode,
              input: {
                thread_id: newThread.id,
                app_state: {
                  pathname: window.location.pathname,
                  fullscreen_duck: false,
                },
                message: content.utterance,
                user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                user_locale: getUserLocale(),
                viewport_is_mobile: viewportIsMobile,
              },
              headers: getPydanticAIChatHeaders(),
              onEvent: eventHandler,
              onClientAction,
            });
          } catch (err) {
            // remove from map so next patch can retry
            subthreadCreations.delete(threadId);
            throw err;
          }
        })();

        subthreadCreations.set(threadId, creationPromise);
        await creationPromise;
      } catch (err) {
        console.error("Failed to create subthread:", err);
        // clear streaming state so input isn't locked
        const content = message.content as SubthreadContent;
        if (content.thread_id) {
          subthreadManager.setStreaming(content.thread_id, false);
        }

        // store error in metadata so UI can display it
        if (!message.metadata) {
          message.metadata = {};
        }

        message.metadata.creation_error =
          err instanceof Error
            ? err.message
            : t("duck:subthread_create_failed");
      }
    })
  );
};

/**
 * Extract chat messages from thread messages.
 * Includes "human", "ai", "tool_call", and "tool_response" messages.
 */
export const extractChatMessages = (
  threadMessagesResponse: ListThreadMessagesResponse
): ChatMessage[] =>
  threadMessagesResponse.messages
    .filter(({ type }) => isRenderableMessageType(type))
    .map((msg) => {
      let state: AssistantMessage | undefined;
      let content = msg.content;
      let backQuack;

      // for AI messages, extract state from content
      // only set state for structured payloads (objects with messages array)
      if (msg.type === ChatMessageRole.AI && content) {
        const contentObj = content as any;

        if (
          contentObj &&
          typeof contentObj === "object" &&
          Array.isArray(contentObj.messages)
        ) {
          const messages = extractMessageContents(msg);
          state = { messages };
          backQuack = contentObj.back_quack;

          // extract markdown for content field (backwards compatibility)
          const markdownMessage = (messages[0] as any)?.markdown_message ?? "";
          content = markdownMessage || content;
        }
      }

      return {
        id: msg.id,
        role: msg.type as ChatMessageRoleType,
        content,
        backQuack,
        state,
        metadata: msg.metadata,
        safeMode: msg.metadata?.safe_mode === true,
        endedAt:
          msg.type === ChatMessageRole.AI
            ? new Date(msg.timestamp).getTime()
            : undefined,
      };
    });

/**
 * Extract and set messages from thread data.
 * This function extracts chat messages from a thread and updates the messages state.
 */
export const extractAndSetMessages = (
  threadMessagesResponse: ListThreadMessagesResponse,
  isStreaming: boolean,
  setMessages: (messages: ChatMessage[]) => void
): void => {
  if (!threadMessagesResponse || isStreaming) return;

  const chatMessages = extractChatMessages(threadMessagesResponse);
  // Avoid clearing the UI with an empty array during refreshes
  if (chatMessages.length === 0) return;

  setMessages(chatMessages);
};

/**
 * Create a user message object for immediate UI display.
 */
export const createUserMessage = (
  text: string,
  safeMode: boolean,
  attachments?: UserContentStructure[]
): ChatMessage => {
  const trimmedText = text.trim();
  const normalizedAttachments = (attachments ?? []).filter(Boolean);

  return {
    id: crypto.randomUUID(),
    role: ChatMessageRole.HUMAN,
    isOptimistic: true,
    safeMode,
    content: {
      elements: [{ type: "text", text: trimmedText }, ...normalizedAttachments],
    },
  };
};

/**
 * Create an assistant message object for streaming.
 */
export const createAssistantMessage = (safeMode: boolean): ChatMessage => ({
  id: crypto.randomUUID(),
  role: ChatMessageRole.AI,
  content: "",
  state: { messages: [] },
  bytes: 0,
  startedAt: Date.now(),
  safeMode,
});

/**
 * Checks if the container is scrolled to the bottom within a small threshold.
 *
 * @param containerRef - The container element
 * @param offset - Pixels from bottom to consider "at bottom" e.g., when using spacers (default: 0)
 * @returns boolean - True if scrolled to bottom
 */
export const isScrolledToBottom = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  offset: number = 0
): boolean => {
  if (!containerRef.current) return false;

  const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

  return (
    scrollTop + clientHeight >=
    scrollHeight - offset - DEFAULT_CHAT_BOTTOM_SCROLL_PADDING
  );
};

/**
 * Scrolls the container to the bottom.
 *
 * @param containerRef - The container element
 * @param threshold - Pixels from bottom to consider "at bottom" (default: 10)
 */
export const scrollToBottom = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  threshold: number = 10
) => {
  if (!containerRef.current) return;

  const { scrollHeight, clientHeight } = containerRef.current;
  const maxScrollTop = scrollHeight - clientHeight;

  containerRef.current.scrollTo({
    top: maxScrollTop - threshold,
    behavior: "smooth",
  });
};

/**
 * Scrolls to the given messageRef within the container.
 *
 * @param containerRef - The container element
 * @param messageRef - The message element
 * @returns void
 */
export const scrollToMessage = async (
  containerRef: React.RefObject<HTMLDivElement | null>,
  messageRef: React.RefObject<HTMLDivElement | null>
) => {
  await sleep(150);

  // isConnected guards against the target element having been unmounted
  // during the sleep (e.g., when a server sync replaces messages mid-scroll).
  if (
    !containerRef.current ||
    !messageRef.current ||
    !messageRef.current.isConnected
  ) {
    return;
  }

  const scrollTop = messageRef.current.offsetTop;

  containerRef.current.scrollTo({
    top: scrollTop,
    behavior: "smooth",
  });
};
/**
 * Calculates the spacer height based on the container, message, and spacer elements.
 * The spacer height is the height of the spacer element that is used to push the
 * messages up so that the user message is at the top of the container when submitted.
 *
 * @param containerRef - The container element
 * @param messageRef - The message element
 * @param spacerRef - The spacer element
 * @returns number - The spacer height
 */
export const calculateSpacerHeight = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  messageRef: React.RefObject<HTMLDivElement | null>,
  spacerRef: React.RefObject<HTMLDivElement | null>
) => {
  if (!containerRef.current || !spacerRef.current || !messageRef.current) {
    return 0;
  }

  const containerHeight = containerRef.current.clientHeight;
  const spacerTop = spacerRef.current.offsetTop;
  const userMessageTop = messageRef.current.offsetTop;

  return Math.max(containerHeight - (spacerTop - userMessageTop), 0);
};

export const validateChatAttachment = (file: File) => {
  if (
    isSupportedFileType(
      file,
      CHAT_FILE_ALLOWED_MIME_TYPE_SET,
      CHAT_FILE_ALLOWED_EXTENSION_SET
    )
  ) {
    return { isValid: true };
  }

  return { isValid: false, errorKey: "unsupported_file_type" };
};

const estimateImageTokens = async (file: File): Promise<number> => {
  const bitmap = await createImageBitmap(file);
  const pixels = bitmap.width * bitmap.height;
  bitmap.close();

  return Math.ceil(pixels / CHAT_FILE_PIXELS_PER_TOKEN);
};

const estimateNonImageTokens = (file: File): number =>
  Math.ceil(file.size / CHAT_FILE_TEXT_BYTES_PER_TOKEN);

/**
 * Estimates total token consumption for a set of files.
 * Images are estimated from pixel dimensions; all other files from byte count.
 * Throws if an image cannot be decoded — callers should handle this.
 */
export const estimateTotalFileTokens = async (
  files: File[]
): Promise<number> => {
  const tokenEstimates = await Promise.all(
    files.map((file) =>
      CHAT_FILE_IMAGE_MIME_TYPES.has(file.type.toLowerCase())
        ? estimateImageTokens(file)
        : Promise.resolve(estimateNonImageTokens(file))
    )
  );

  return tokenEstimates.reduce((sum, t) => sum + t, 0);
};

export const formatHumanMessage = (message: ChatMessage): string => {
  if (message.role !== ChatMessageRole.HUMAN) {
    return "";
  }

  if (typeof message.content === "string") {
    // This provides backwards compatibility for the legacy server response format,
    // which was the format before we supported attachments.
    // This format can remain in saved threads for an indefinite period of time.
    return message.content;
  }

  if (
    typeof message.content === "object" &&
    message.content !== null &&
    "elements" in message.content &&
    Array.isArray(message.content.elements) &&
    message.content.elements.length > 0 &&
    message.content.elements[0].type === "text"
  ) {
    // This is the standard server response format.
    // The first element is the text content.
    // If there are attachments, they follow.
    return message.content.elements[0].text;
  }

  return JSON.stringify(message.content);
};
